import { z } from 'zod'

// Zod 4 compila los objetos con `new Function`, que la CSP del portal bloquea.
z.config({ jitless: true })

/**
 * Contratos de la pestaña Planificación (Fase 5a): el plan de contenido de un
 * período, con su documento en Google Docs, que pasa por el mismo circuito de
 * revisión que una publicación.
 */

export const TOPE_TITULO_PLAN = 80
export const TOPE_DESCRIPCION_PLAN = 2000

const fecha = (mensaje: string) =>
  z.string({ error: mensaje }).regex(/^\d{4}-\d{2}-\d{2}$/, mensaje)

/** Una URL http(s) cualquiera; que sea de Google Docs se advierte, no se exige. */
export const urlValida = (mensaje: string) =>
  z
    .string({ error: mensaje })
    .trim()
    .min(1, mensaje)
    .max(500, 'El enlace: hasta 500 caracteres.')
    .refine((v) => {
      try {
        const u = new URL(v)
        return u.protocol === 'https:' || u.protocol === 'http:'
      } catch {
        return false
      }
    }, mensaje)

export const planificacionEntrada = z
  .object({
    proyectoId: z.uuid(),
    titulo: z
      .string({ error: 'Poné un título.' })
      .trim()
      .min(1, 'Poné un título.')
      .max(TOPE_TITULO_PLAN, `El título: hasta ${String(TOPE_TITULO_PLAN)} caracteres.`),
    fechaDesde: fecha('Elegí desde cuándo.'),
    fechaHasta: fecha('Elegí hasta cuándo.'),
    descripcion: z
      .string()
      .max(
        TOPE_DESCRIPCION_PLAN,
        `La descripción: hasta ${String(TOPE_DESCRIPCION_PLAN)} caracteres.`,
      )
      .default(''),
    urlDocumento: urlValida('Pegá el enlace del documento.'),
    /** Al crear: guardar como borrador o enviar a revisión directamente. */
    enviar: z.boolean().default(false),
  })
  .refine((d) => d.fechaHasta >= d.fechaDesde, {
    message: 'La fecha de fin no puede ser anterior a la de inicio.',
    path: ['fechaHasta'],
  })
export type PlanificacionEntrada = z.input<typeof planificacionEntrada>

export type EstadoPlanificacion = 'borrador' | 'para_revisar' | 'cambios' | 'aprobada'

export interface PlanificacionResumen {
  id: string
  titulo: string
  fechaDesde: string
  fechaHasta: string
  descripcion: string
  urlDocumento: string
  estado: EstadoPlanificacion
  comentarios: number
  nuevo: boolean
  dadaDeBaja: boolean
}

export interface ListaPlanificaciones {
  planificaciones: PlanificacionResumen[]
  /** Para precargar el período de la próxima: el mes siguiente al de la última. */
  proximoDesde: string
  proximoHasta: string
}

export const ACCIONES_PLAN = [
  'cargo',
  'envio_a_revision',
  'comento',
  'pidio_cambios',
  'aprobo',
  'corregida',
  'cambio_documento',
  'dio_de_baja',
  'restauro',
] as const
export type AccionPlanificacion = (typeof ACCIONES_PLAN)[number]

export interface ComentarioPlan {
  id: string
  autor: { id: string; nombre: string; tipo: 'cliente' | 'rastro' }
  texto: string
  resuelto: boolean
  creadoEn: string
}

export interface PanelPlanificacion extends PlanificacionResumen {
  proyectoId: string
  comentariosLista: ComentarioPlan[]
  /** "Creada el 28 de septiembre" / "Aprobada por Valentina el 2 de octubre". */
  contexto: string
  comentoEnEstaRonda: boolean
}

/** La franja del panel, según estado y quién mira. */
export function franjaDePlanificacion(
  estado: EstadoPlanificacion,
  esRastro: boolean,
): string | null {
  if (esRastro) {
    if (estado === 'borrador') return 'Borrador. El cliente todavía no la ve.'
    if (estado === 'para_revisar') return 'Esperando la revisión del cliente.'
    if (estado === 'cambios')
      return 'El cliente pidió cambios. Corregí el documento y marcala como corregida.'
    return null
  }
  if (estado === 'para_revisar')
    return 'Leé el plan del mes y comentá lo que quieras cambiar, o aprobalo. Tenés una sola revisión.'
  if (estado === 'cambios')
    return 'RASTRO está corrigiendo el documento. Cuando lo marque como corregido, queda aprobado.'
  return null
}

/**
 * El identificador de un documento de Google a partir de su enlace
 * (docs.google.com/document/d/<id>/…). Sirve para preguntar a Drive si lo ve
 * cualquiera con el enlace, igual que con los archivos de las publicaciones.
 */
export function idDeDocumentoGoogle(url: string): string | null {
  const m = /\/d\/([\w-]{20,})/.exec(url)
  return m?.[1] ?? null
}

export const esDeGoogleDocs = (url: string) => {
  try {
    return new URL(url).hostname === 'docs.google.com'
  } catch {
    return false
  }
}
